import {
	Box,
	ButtonGroup,
	Flex,
	FormControl,
	FormLabel,
	Icon,
	Input,
	Text,
	useColorModeValue,
	useDisclosure,
} from '@chakra-ui/react';
import { IconCaretDown, IconCaretRight } from '@tabler/icons';
import { ButtonComponent } from 'components/buttons/ButtonComponent';
import Card from 'components/card/Card';
import GenericInput from 'components/inputs/GenericInput';
import { LoadingSpinner } from 'components/loading/Loading';
import { RouterPrompt } from 'components/modal/RouterPrompt';
import TreeView from 'components/treeView/TreeView';
import { SidebarHelpContext } from 'contexts/SidebarHelpContext';
import { ISidebarHelp } from 'models/sidebarHelp/sidebarHelp.model';
import moment from 'moment';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'services/hooks/useForm';
import { useEditProductionTarget } from 'services/queryClient/wrapperHooks/useEditProductionTarget';
import { useGetProductionTarget } from 'services/queryClient/wrapperHooks/useGetProductionTarget';

import { ModalSave } from './components/ModalSave';
import { helpContent } from './variables/helpContent';
import { productionTargetMessages } from './variables/productionTargetMessages';

interface IProductionTargetItem {
	id: number | null;
	recurso_id: number;
	descricao_recurso?: string;
	mes_ano: string | Date;
	empresa_id: number | null;
	disponibilidade_meta: number;
	performance_meta: number;
	qualidade_meta: number;
	irog_meta: number;
	updateYear?: boolean;
}

interface IProductionTargetGroup {
	nome_grupo_recurso: string;
	id_grupo: number;
	recursos: IProductionTargetItem[];
}

interface IColumnConfig {
	text: string;
}

export default function CadastroMetas() {
	const { setters } = useContext(SidebarHelpContext);

	const { setDataSidebar } = setters;

	const helpMetas: ISidebarHelp = useMemo(() => helpContent, []);

	useEffect(() => {
		setDataSidebar(helpMetas);
	}, [helpMetas, setDataSidebar]);

	const { form, onChange, setForm } = useForm({});

	const { onClose: onCloseConfirmSave } = useDisclosure();
	const {
		isOpen: isOpenModalSaveYear,
		onOpen: onOpenModalSaveYear,
		onClose: onCloseModalSaveYear,
	} = useDisclosure();
	const [closeSave, setCloseSave] = useState(false);
	const [saveYear, setSaveYear] = useState();

	const [items, setItems] = useState<any>({
		root: {
			index: 'root',
			children: [],
			data: 'Root item',
			hasChildren: true,
			indexComponent: 0,
		},
	});

	const date = new Date();
	const currentYear = date.getFullYear();
	const currentMonth = date.getMonth() + 1;

	const [filterProductionTarget, setFilterProductionTarget] = useState({
		year: currentYear,
		month: currentMonth,
	});

	const {
		mutate: getProductionTargetMutate,
		data: ProductionTargetData,
		isLoading: isLoadingProductionTarget,
		isError: isErrorProductionTarget,
	} = useGetProductionTarget(
		filterProductionTarget.year,
		filterProductionTarget.month
	);

	useEffect(() => {
		getProductionTargetMutate();
	}, [getProductionTargetMutate]);

	useEffect(() => {
		if (ProductionTargetData) {
			const newItems: any = {
				root: {
					index: 'root',
					children: [],
					data: 'Root item',
					hasChildren: true,
					indexComponent: 0,
				},
			};

			ProductionTargetData?.forEach(
				(item: IProductionTargetGroup, index: number) => {
					const keyChannel = item.nome_grupo_recurso;
					newItems.root.children.push(keyChannel);
					newItems[keyChannel] = {
						id: item.id_grupo,
						index: keyChannel,
						children: [],
						data: item.nome_grupo_recurso,
						hasChildren: true,
						indexComponent: 1,
						isOpen: false,
						idElement: item.id_grupo,
						original: item,
					};

					item.recursos.forEach(
						(subItem: IProductionTargetItem, subIndex: number) => {
							const keyFamily = `${keyChannel}-${subItem.descricao_recurso}`;
							newItems[keyChannel].children.push(keyFamily);
							newItems[keyFamily] = {
								id: subItem.recurso_id,
								index: keyFamily,
								children: [],
								data: subItem.descricao_recurso,
								hasChildren: true,
								indexComponent: 2,
								isOpen: false,
								idElement: item.id_grupo,
								isChildrenOfChannel: keyChannel,
								isChildrenOfFamily: subItem.descricao_recurso,
								original: subItem,
							};
						}
					);
				}
			);

			setItems(newItems);
		}
	}, [ProductionTargetData]);

	const [objToUpdate, setObjToUpdate] = useState<IProductionTargetItem[]>([]);

	useEffect(() => {
		if (ProductionTargetData) {
			const newObj = {};
			const newObjToUpdate: IProductionTargetItem[] = [];

			ProductionTargetData?.forEach(
				(item: IProductionTargetGroup, index: number) => {
					const keyChannel = item.nome_grupo_recurso;
					newObj[keyChannel] = {};

					item.recursos.forEach(
						(subItem: IProductionTargetItem, subIndex: number) => {
							const keyFamily = subItem.descricao_recurso;
							newObjToUpdate.push(subItem);
							newObj[keyChannel][keyFamily] = {
								DISPONIBILIDADE: subItem.disponibilidade_meta,
								QUALIDADE: subItem.qualidade_meta,
								PERFORMANCE: subItem.performance_meta,
								IROG: `${subItem.irog_meta}%`,
								id: subItem.id,
								recurso_id: subItem.recurso_id,
								empresa_id: 1,
								save_year: saveYear,
								mes_ano: subItem.mes_ano,
							};
						}
					);
				}
			);

			setObjToUpdate(newObjToUpdate);
			setForm(newObj);
		}
	}, [ProductionTargetData, saveYear, setForm]);

	const columnsProductionTarget = [
		{ text: productionTargetMessages.LABELS.AVAILABILITY },
		{ text: productionTargetMessages.LABELS.PERFORMANCE },
		{ text: productionTargetMessages.LABELS.QUALITY },
		{ text: productionTargetMessages.LABELS.IROG },
	];

	const [focus, setFocus] = useState({
		ProductionTargetId: '',
		column: '',
	});

	const [stateForBody, setStateForBody] = useState<any>([]);

	const { mutate } = useEditProductionTarget(
		stateForBody,
		setCloseSave,
		getProductionTargetMutate
	);

	const updateData = () => {
		setCloseSave(false);
		onCloseModalSaveYear();
		mutate();
	};

	const updateProductionTarget = useCallback(
		(event, isChildrenOfChannel?, item?) => {
			event.preventDefault();
			setCloseSave(true);

			const { name, value } = event.target;

			const family = {
				...form[isChildrenOfChannel],
				[item]: { ...form[isChildrenOfChannel][item], [name]: value },
			};

			const qualidade = Number(family[item]['QUALIDADE']) / 100;
			const performance = Number(family[item]['PERFORMANCE']) / 100;
			const diponibilidade =
				Number(family[item]['DISPONIBILIDADE']) / 100;

			family[item]['IROG'] = (
				diponibilidade *
				performance *
				qualidade *
				100
			).toFixed(2);

			/**
			 * Necessário filtrar para evitar que os recursos sejam duplicados. Ocorre que a função está sendo
			 * chamada duas vezes quando o usuário altera o valor do input.
			 */
			const createObjectToUpdate = objToUpdate
				.map((recurso, index) => {
					if (recurso.recurso_id === family[item].recurso_id) {
						return (objToUpdate[index] = {
							disponibilidade_meta: Number(
								family[item]['DISPONIBILIDADE']
							),
							performance_meta: Number(
								family[item]['PERFORMANCE']
							),
							qualidade_meta: Number(family[item]['QUALIDADE']),
							irog_meta: Number(family[item]['IROG']),
							mes_ano: family[item]['mes_ano'],
							updateYear: family[item]['save_year'],
							id: recurso.id,
							empresa_id: family[item]['empresa_id'],
							recurso_id: family[item]['recurso_id'],
						});
					} else {
						return recurso;
					}
				})
				.filter(
					(value, index, array) =>
						array.findIndex(
							el => el?.recurso_id === value?.recurso_id
						) === index
				);

			setStateForBody(createObjectToUpdate);
		},
		[form, objToUpdate]
	);

	useEffect(() => {
		stateForBody?.forEach(element => {
			return (element.updateYear = saveYear);
		});
	}, [saveYear, stateForBody]);

	const inputProducts = (column, item, indexItem, isChildrenOfChannel) => {
		return (
			<Flex
				h={12}
				alignItems={'center'}
				key={column.id}
				borderBottom={item !== 'Total' ? '1px solid' : 'null'}
				borderColor={item !== 'Total' ? borderColor : 'null'}
			>
				<FormControl
					key={column.id}
					display={'flex'}
					justifyContent={'center'}
				>
					<Input
						w={20}
						autoFocus={
							focus.ProductionTargetId === item &&
							focus.column === column.text
						}
						px={4}
						type='number'
						border={'1px solid'}
						borderColor={'secondaryGray.100'}
						borderRadius={'16px'}
						value={form[isChildrenOfChannel][item][column.text]}
						onChange={e => {
							setFocus({
								ProductionTargetId: item,
								column: column.text,
							});
							onChange(
								e,
								'meta-producao',
								column.text,
								indexItem,
								isChildrenOfChannel,
								'',
								item
							);
							updateProductionTarget(
								e,
								isChildrenOfChannel,
								item
							);
						}}
						name={column.text}
					/>
				</FormControl>
			</Flex>
		);
	};

	const columnInputs = (
		item: string,
		indexItem: string,
		isChildrenOfChannel,
		isChildrenOfFamily
	) =>
		columnsProductionTarget.map((column: IColumnConfig, index: number) => {
			switch (index) {
				case 3:
					return (
						<Flex
							h={12}
							alignItems={'center'}
							borderBottom={'1px solid'}
							borderColor={borderColor}
						>
							<FormControl
								key={index}
								mx={3}
							>
								<Input
									px={0}
									type='string'
									value={
										form[isChildrenOfChannel][item][
											column.text
										]
									}
									border={'none'}
									color={
										item !== 'Total'
											? 'secondaryGray.500'
											: 'easyBLUE.300'
									}
									fontWeight={500}
									fontSize={'14px'}
									textAlign={'center'}
									isReadOnly={true}
									borderRadius={'16px'}
								/>
							</FormControl>
						</Flex>
					);
				default:
					return inputProducts(
						column,
						item,
						indexItem,
						isChildrenOfChannel
					);
			}
		});

	const displaycolumnsAbbreviated = (
		isChildrenOfChannel,
		isChildrenOfFamily
	) =>
		columnsProductionTarget.map((column: IColumnConfig, index) => {
			if (index === 3) {
				return (
					<Flex
						h={12}
						alignItems={'center'}
						justifyContent={'center'}
						borderBottom={'1px solid'}
						borderColor={borderColor}
					>
						<Text
							key={index}
							mx={6}
							fontSize={'sm'}
							color={'secondaryGray.500'}
							textAlign={'center'}
							fontWeight={700}
						>
							{column.text.toUpperCase()}
						</Text>
					</Flex>
				);
			}
			return (
				<Flex
					h={12}
					alignItems={'center'}
					justifyContent={'center'}
					borderBottom={'1px solid'}
					borderColor={borderColor}
				>
					<Text
						key={index}
						mx={6}
						fontSize={'sm'}
						textAlign={'center'}
						fontWeight={700}
						// color={((form[column.abbreviated].Total) >= 99 && (form[column.abbreviated].Total) <= 101) ? "green.500" : "secondaryGray.500"}
					>
						{column.text}
					</Text>
				</Flex>
			);
		});

	const onChangeFilter = (valueYear, valueMonth) => {
		setFilterProductionTarget({ year: valueYear, month: valueMonth });
		getProductionTargetMutate();
	};

	const setOpen = (id: string) => {
		setItems({
			...items,
			[id]: { ...items[id], isOpen: !items[id].isOpen },
		});
	};

	const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

	const renderYears = useMemo(() => {
		let initialYear = moment().subtract(1, 'years').year();
		const finalYear = moment().add(4, 'years').year();

		const years = [] as any;

		do {
			years.push(
				<option
					key={initialYear}
					value={initialYear}
					selected={filterProductionTarget?.year === initialYear}
				>
					{initialYear}
				</option>
			);

			initialYear++;
		} while (initialYear <= finalYear);

		return years;
	}, [filterProductionTarget?.year]);

	const renderMonths = useMemo(() => {
		const months = [
			{ value: 1, label: productionTargetMessages.MONTHS.JANUARY },
			{ value: 2, label: productionTargetMessages.MONTHS.FEBRUARY },
			{ value: 3, label: productionTargetMessages.MONTHS.MARCH },
			{ value: 4, label: productionTargetMessages.MONTHS.APRIL },
			{ value: 5, label: productionTargetMessages.MONTHS.MAY },
			{ value: 6, label: productionTargetMessages.MONTHS.JUNE },
			{ value: 7, label: productionTargetMessages.MONTHS.JULY },
			{ value: 8, label: productionTargetMessages.MONTHS.AUGUST },
			{ value: 9, label: productionTargetMessages.MONTHS.SEPTEMBER },
			{ value: 10, label: productionTargetMessages.MONTHS.OCTOBER },
			{ value: 11, label: productionTargetMessages.MONTHS.NOVEMBER },
			{ value: 12, label: productionTargetMessages.MONTHS.DECEMBER },
		];

		return months.map(month => (
			<option
				key={month.value}
				value={month.value}
				selected={filterProductionTarget?.month === month.value}
			>
				{month.label}
			</option>
		));
	}, [filterProductionTarget?.month]);

	return (
		<Flex
			direction={{ base: 'column', xl: 'row' }}
			pt={{ base: '130px', md: '80px', xl: '80px' }}
		>
			<Card pt={10}>
				<Box mb={5}>
					<Box mb={14}>
						<Flex gap={4}>
							<FormControl
								mr='5px'
								mb='20px'
							>
								<FormLabel mb='0'>
									{productionTargetMessages.LABELS.YEAR}
								</FormLabel>
								<GenericInput
									type='select'
									inputProps={{
										onChange: e => {
											onChangeFilter(
												e.target.value,
												filterProductionTarget.month
											);
										},
										h: '44px',
										borderRadius: 10,
										selectOptions: renderYears,
									}}
								/>
							</FormControl>

							<FormControl
								mr='5px'
								mb='20px'
							>
								<FormLabel mb='0'>
									{productionTargetMessages.LABELS.MONTH}
								</FormLabel>
								<GenericInput
									type='select'
									inputProps={{
										onChange: e => {
											onChangeFilter(
												filterProductionTarget.year,
												e.target.value
											);
										},
										h: '44px',
										borderRadius: 10,
										selectOptions: renderMonths,
									}}
								/>
							</FormControl>
						</Flex>
					</Box>
					{isLoadingProductionTarget && <LoadingSpinner />}
					{isErrorProductionTarget && (
						<h1>{productionTargetMessages.MSG_ERROR_LOADING}</h1>
					)}
					{ProductionTargetData && (
						<TreeView
							ids={[]}
							id='root'
							Containers={[
								({ data, children }) => (
									<Box>
										<>{children}</>
									</Box>
								),
								({
									data,
									id,
									children,
									isChildrenOfChannel,
									isChildrenOfFamily,
								}) => (
									<Box
										display={'grid'}
										gridTemplateColumns={`15rem repeat(4, 1fr)`}
										gridRowGap={0.5}
										alignItems={'center'}
									>
										<Flex
											onClick={() => setOpen(id)}
											// paddingLeft={24}
											fontSize={'16px'}
											fontWeight={400}
											color={'easyBLUE.300'}
											h={12}
											alignItems={'center'}
											borderBottom={'1px solid'}
											borderColor={borderColor}
										>
											<Flex mr={3.5}>
												{items[id].isOpen ? (
													<Icon
														as={IconCaretDown}
														boxSize={5}
														color={
															'secondaryGray.600'
														}
													/>
												) : (
													<Icon
														as={IconCaretRight}
														boxSize={5}
														color={
															'secondaryGray.600'
														}
													/>
												)}
											</Flex>
											<Text>{data}</Text>
										</Flex>
										{displaycolumnsAbbreviated(
											isChildrenOfChannel,
											isChildrenOfFamily
										)}

										{items[id].isOpen && children}
									</Box>
								),

								({
									data,
									id,
									children,
									index,
									idElement,
									isChildrenOfChannel,
									isChildrenOfFamily,
								}) => (
									<>
										<Box
											alignItems={'center'}
											borderBottom={
												data !== 'Total'
													? '1px solid'
													: 'null'
											}
											borderColor={
												data !== 'Total'
													? borderColor
													: 'null'
											}
										>
											<Flex
												onClick={() => setOpen(id)}
												paddingLeft={15}
												fontSize={'16px'}
												fontWeight={400}
												color={'easyBLUE.300'}
												h={12}
												alignItems={'center'}
												bg={
													data === 'Total'
														? 'secondaryGray.300'
														: 'white'
												}
											>
												<Text>{data}</Text>
											</Flex>
										</Box>

										{columnInputs(
											data,
											index,
											isChildrenOfChannel,
											isChildrenOfFamily
										)}
									</>
								),
							]}
							items={items}
							deeper={0}
						/>
					)}
				</Box>
				<Flex
					flexDirection={'row'}
					justifyContent={'flex-end'}
					mt={'20px'}
					me={'20px'}
				>
					<ButtonGroup spacing={'24px'}>
						<ButtonComponent
							type={'ghost'}
							title={productionTargetMessages.LABELS.CANCEL}
						/>
						<ButtonComponent
							data-test='button-salvar'
							type={'primary'}
							title={productionTargetMessages.LABELS.SAVE}
							onClick={onOpenModalSaveYear}
						/>
					</ButtonGroup>
				</Flex>
			</Card>

			<RouterPrompt
				isOpen={closeSave}
				saveChanges={updateData}
				onCloseConfirmSave={onCloseConfirmSave}
			></RouterPrompt>
			<ModalSave
				isOpen={isOpenModalSaveYear}
				saveChanges={updateData}
				onClose={onCloseModalSaveYear}
				setSaveYear={setSaveYear}
			/>
		</Flex>
	);
}
